import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DeliveryZoneRow } from '@shared/api/giftDeliveryZone/giftDeliveryZone.admin';

export const ZONE_HEADERS = ['시도', '시군구', '읍면동', '추가배송비', '배송가능'];
export function makeDeliveryZoneWorkbook(rows: DeliveryZoneRow[] = []) {
  const workbook = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet([
    ZONE_HEADERS,
    ...rows.map((r) => [
      r.sido,
      r.sigungu,
      r.emd,
      r.extraFee,
      r.deliverable ? '가능' : '불가',
    ]),
  ]);
  sheet['!cols'] = [{ wch: 14 }, { wch: 24 }, { wch: 20 }, { wch: 16 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(workbook, sheet, '배송지역');
  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.aoa_to_sheet([
      ['작성 안내'],
      ['배송지역 시트의 첫 행 제목을 유지하고 2행부터 작성하세요. 최대 20,000행입니다.'],
      [
        '시도: 서울, 부산, 대구, 인천, 광주, 대전, 울산, 세종, 경기, 강원, 충북, 충남, 전북, 전남, 경북, 경남, 제주',
      ],
      [
        '시군구와 읍면동은 주소 검색 결과와 같은 이름으로 입력하세요. 시군구는 세종만 비울 수 있습니다.',
      ],
      [
        '추가배송비는 0 이상의 정수(원), 배송가능은 가능 또는 불가로 입력하세요. 수식은 사용할 수 없습니다.',
      ],
      [
        '추가배송비는 기본배송비 면제금액을 넘겨도 부과됩니다. 기본 요금과 면제금액은 브랜드 화면에서 설정합니다.',
      ],
      ['같은 지역에 서로 다른 금액이나 배송가능 여부를 입력하면 등록할 수 없습니다.'],
      [
        '기존 배송불가 지역은 기본적으로 차단 상태를 유지합니다. 재활성화 옵션을 선택하면 엑셀 값으로 변경됩니다.',
      ],
      ['아래는 작성 예시이며 등록 데이터에 포함되지 않습니다.'],
      ZONE_HEADERS,
      ['서울', '강남구', '역삼동', 0, '가능'],
      ['경기', '고양시 덕양구', '행신2동', 3000, '가능'],
      ['세종', '', '가람동', 0, '불가'],
    ]),
    '작성안내',
  );
  return workbook;
}
export function downloadDeliveryZones(rows?: DeliveryZoneRow[]) {
  saveAs(
    new Blob(
      [XLSX.write(makeDeliveryZoneWorkbook(rows), { bookType: 'xlsx', type: 'array' })],
      {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    ),
    rows ? '배송지역_목록.xlsx' : '배송지역_등록양식.xlsx',
  );
}
export function parseDeliveryZoneWorkbook(workbook: XLSX.WorkBook): DeliveryZoneRow[] {
  const sheet =
    workbook.Sheets['배송지역'] ?? workbook.Sheets[workbook.SheetNames[0] ?? ''];
  if (!sheet || !sheet['!ref']) throw new Error('배송지역 시트를 찾을 수 없습니다.');
  const range = XLSX.utils.decode_range(sheet['!ref']);
  if (range.e.r > 20000) throw new Error('한 번에 20,000행까지 등록할 수 있습니다.');
  if (
    !ZONE_HEADERS.every(
      (name, col) => sheet[XLSX.utils.encode_cell({ r: 0, c: col })]?.v === name,
    )
  )
    throw new Error('제공된 양식의 첫 행 제목과 열 순서를 유지해 주세요.');
  const rows: DeliveryZoneRow[] = [];
  const errors: string[] = [];
  for (let r = 1; r <= range.e.r; r++) {
    const cells = ZONE_HEADERS.map((_, c) => sheet[XLSX.utils.encode_cell({ r, c })]);
    if (cells.every((c) => c?.v == null || String(c.v).trim() === '')) continue;
    const values = cells.map((c) => String(c?.v ?? '').trim());
    const [sido = '', sigungu = '', emd = '', fee = '', status = ''] = values;
    let error = '';
    if (cells.some((c) => c?.f)) error = '수식 대신 값을 입력해 주세요.';
    else if (!sido || !emd || (!sigungu && !['세종', '세종특별자치시'].includes(sido)))
      error = '시도·시군구·읍면동을 확인해 주세요.';
    else if (
      !/^\d[\d,]*$/.test(fee) ||
      !Number.isSafeInteger(Number(fee.replace(/,/g, '')))
    )
      error = '추가배송비는 0 이상의 정수여야 합니다.';
    else if (!['가능', '불가'].includes(status))
      error = '배송가능은 가능 또는 불가로 입력해 주세요.';
    if (error) errors.push(`${r + 1}행: ${error}`);
    rows.push({
      rowNumber: r + 1,
      sido,
      sigungu,
      emd,
      extraFee: Number(fee.replace(/,/g, '')),
      deliverable: status === '가능',
    });
  }
  if (errors.length)
    throw new Error(
      `${errors.length}개 행에 오류가 있습니다.\n${errors.slice(0, 20).join('\n')}`,
    );
  if (!rows.length)
    throw new Error('등록할 지역이 없습니다. 배송지역 시트의 2행부터 작성해 주세요.');
  return rows;
}
export async function readDeliveryZoneFile(file: File) {
  if (!/\.xlsx$/i.test(file.name)) throw new Error('.xlsx 파일을 선택해 주세요.');
  if (file.size > 5 * 1024 * 1024) throw new Error('5MB 이하의 파일을 선택해 주세요.');
  return parseDeliveryZoneWorkbook(
    XLSX.read(await file.arrayBuffer(), { type: 'array' }),
  );
}
